import { Config, configurator, useFetchApi, enumsSetting } from "@itarget/shared";
import { LinearProgress } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router, Switch } from "react-router-dom";
import { auth, fidelity, language, setting } from "../redux";
import ChangePasswordContainer from "./changePassword/ChangePasswordContainer";
import DasboardContainer from "./dashboard/DasboardContainer";
import DetailEadContainer from "./detailEad/DetailEadContainer";
import EadContainer from "./ead/EadContainer";
import FidelityDashboardContainer from "./fidelityDashboard/FidelityDashboardContainer";
import LoginContainer from "./login/LoginContainer";
import MeContainer from "./me/MeContainer";
import MyActivityContainer from "./myActivity/MyActivityContainer";
import RouterCourse from "./myCourse/RouterCourse";
import DetailMyLectureContainer from "./myLecture/DetailMyLectureContainer";
import NewPanelContainer from "./newPanel/NewPanelContainer";
import RouterOffers from "./offers/RouterOffers";
import OffersUniqueContainer from "./offersUnique/OffersUniqueContainer";
import PageContainer from "./page/PageContainer";
import PanelContainer from "./panel/PanelContainer";
import PaymentContainer from "./payment/PaymentContainer";
import RouteContainer from "./RouteContainer";
import RecoveryLoginContainer from "./recoveryLogin/RecoveryLoginContainer";
import RedirectContainer from "./redirect/RedirectContainer";
import StoreContainer from "./Store/StoreContainer";
import ScientificWorkContainer from "./scientificWork/ScientificWorkContainer";
import ShoppingCartContainer from "./shoppingCart/ShoppingCartContainer";
import LessonContainer from "./lesson/LessonContainer";
import StreamingContainer from "./streaming/StreamingContainer";
import ToasterContainer from "./ToasterContainer";

function AppContainer() {
	const dispatch = useDispatch();
	const [status, setStatus] = useState();
	const user = useSelector((state) => state.auth.user);
	const token = useSelector((state) => state.fetch.token);
	const centerCostId = useSelector((state) => state.auth.cc);
	const SelectedLang = useSelector((state) => state.language.lang);
	const systemTheme = useSelector((state) => state.theme.systemTheme);

	const getColorBySystemTheme = (light, dark, important = true) => {
		if (!dark) return light;
		return `${systemTheme == "light" ? light : dark}${
			important ? " !important" : ""
		}`;
	};

	const theme = useMemo(
		() =>
			createTheme({
				typography: {
					allVariants: {
						color: getColorBySystemTheme("#000000de", "#fff"),
					},
					button: {
						color: "inherit !important",
					},
					offersTitle: {
						fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
						fontWeight: 400,
						fontSize: "0.875rem",
						lineHeight: 1.3,
						letterSpacing: "0.01071em",
					},
					limitNumberOfLines: (numberOfLines = 1) => {
						return {
							display: "-webkit-box",
							"-webkit-line-clamp": numberOfLines,
							"-webkit-box-orient": "vertical",
							wordBreak: "none",
							overflow: "hidden",
							hyphens: "auto",
							"--is-single-line": `1 - Clamp(0, Calc(${numberOfLines} - 1), ${numberOfLines})`,
							"--delay": "Calc(-1s * (var(--is-single-line, 1) - 1))",
							animation: "states 1s var(--delay) paused",
						};
					},
				},
				breakpoints: {
					values: {
						xs: 500,
						sm: 700,
						md: 960,
						lg: 1200,
						xl: 1500,
					},
				},
				palette: {
					getColorBySystemTheme,
					transparent: {
						light: getColorBySystemTheme("#ffffff50"),
						main: getColorBySystemTheme("#ffffff50", "#00000050"),
						dark: getColorBySystemTheme("#00000050"),
					},
					primary: {
						main: getColorBySystemTheme("#1976d2", "#1976d2", false),
						contrastText: getColorBySystemTheme("#fff"),
					},
					secondary: {
						main: getColorBySystemTheme("#4B5563", "#404943"),
						contrastText: getColorBySystemTheme("#E5E7EB", "#E5E7EB"),
					},
					even: {
						main: getColorBySystemTheme("#1c608a", "#2a8ac7", false),
						contrastText: getColorBySystemTheme("#fff"),
					},
					odd: {
						main: getColorBySystemTheme("#f28704", "#ff9a1a", false),
						contrastText: getColorBySystemTheme("#fff"),
					},
					error: {
						main: getColorBySystemTheme("#E32f2f", "#ff8b7f"),
						contrastText: getColorBySystemTheme("#fff", "#690005"),
					},
					warning: {
						main: getColorBySystemTheme("#E65100", "#EF6C00"),
						contrastText: getColorBySystemTheme("#000000ee", "#000000ee"),
					},
					info: {
						main: getColorBySystemTheme("#0D47A1", "#039BE5"),
						contrastText: getColorBySystemTheme("#fff", "#000000ee"),
					},
					success: {
						main: getColorBySystemTheme("#1B5E20", "#a2ffa6"),
						contrastText: getColorBySystemTheme("#fff"),
					},
					midnight: {
						main: getColorBySystemTheme("#E0E2EC", "#404943"),
						dark: getColorBySystemTheme("#F1F1F1", "#ffffff14"),
						contrastText: getColorBySystemTheme("#43474E", "#FFFFFF"),
					},
					twilight: {
						main: getColorBySystemTheme("#EEEEEE", "#434343"),
						dark: getColorBySystemTheme("#E0E0E0", "#616161"),
						contrastText: getColorBySystemTheme("#424242", "#FFFFFF"),
					},
					loading: {
						color: getColorBySystemTheme("#424242", "#FFFFFF"),
						iconColor: getColorBySystemTheme("#424242", "#FFFFFF"),
						backgroundColor: getColorBySystemTheme("#EEEEEE", "#212121"),
					},
					alert: {
						color: getColorBySystemTheme("#4A044E", "#d8c7e9"),
						backgroundColor: getColorBySystemTheme("#FAF5FF", "#201d21"),
					},
					header: {
						menu: {
							backgroundColor: getColorBySystemTheme("#fff", "#232323"),
							backgroundColorShadow: getColorBySystemTheme(
								"#fff",
								"#232323",
								false,
							),
						},
						menuItem: {
							color: getColorBySystemTheme("#000", "#fff"),
							backgroundHover: getColorBySystemTheme("#c2c2bd3b", "#433f43"),
							backgroundActive: getColorBySystemTheme("#c2c2c23b", "#434343"),
						},
						userContainer: {
							backgroundColor: getColorBySystemTheme("#ffffff66", "#21212166"),
						},
						shoppingCart: {
							color: getColorBySystemTheme("#FF5252", "#FF5252"),
							badgeColor: getColorBySystemTheme("#E65100", "#EF6C00"),
						},
						avatar: {
							color: getColorBySystemTheme("#fff", "#fff"),
							backgroundColor: getColorBySystemTheme("#1976D2", "#1976D2"),
						},
						participantAccess: {
							color: getColorBySystemTheme("#424242", "#fdfdfd"),
							buttonColor: getColorBySystemTheme("#fff ", "#fff"),
							backgroundColor: getColorBySystemTheme("transparent", "#131313"),
							buttonBackgroundColor: getColorBySystemTheme(
								"#1976D2",
								"#1976D2",
							),
							buttonBackgroundColorHover: getColorBySystemTheme(
								"#1979D2",
								"#1973D2",
							),
						},
						sub: {
							color: getColorBySystemTheme("#000", "#fff"),
							border: getColorBySystemTheme("#e0e0e0", "#424242"),
						},
					},
					programingOnDemandGrouped: {
						headerBorder: getColorBySystemTheme("#E0E0E0", "#424242"),
						module: {
							backgroundColor: getColorBySystemTheme("#F3F3F3", "#333333"),
							color: getColorBySystemTheme("#000000", "#FFFFFF"),
						},
						subModule: {
							backgroundColor: getColorBySystemTheme("#F7F7F7", "#262626"),
							color: getColorBySystemTheme("#424242", "#E0E0E0"),
						},
						session: {
							backgroundColor: getColorBySystemTheme("#FFFFFF", "#1A1A1A"),
							color: getColorBySystemTheme("#616161", "#BDBDBD"),
						},
					},
					dateFilterPicker: {
						container: {
							border: getColorBySystemTheme("#3F80D6", "#2E3035"),
							backgroundColor: getColorBySystemTheme(
								"transparent",
								"#434343",
								false,
							),
						},
						text: {
							color: getColorBySystemTheme("#3F80D6", "#fff"),
							colorSelected: getColorBySystemTheme("#fff"),
						},
					},
					mainShowcase: {
						items: {
							backgroundColor: getColorBySystemTheme("#F9F9FF", "#232323"),
						},
					},
					cardToPayment: {
						backgroundColor: getColorBySystemTheme("#ffffff", "#212121"),
						textColor: getColorBySystemTheme("#191C20", "#E1E2E9"),
						labelColor: getColorBySystemTheme("#616161", "#8D9199"),
						borderColor: getColorBySystemTheme("#F0F0F7", "#232323"),
					},
					displayValuesOffers: {
						root: {
							backgroundColor: getColorBySystemTheme("#F3F3F3", "#212121"),
						},
						header: {
							axiosColor: getColorBySystemTheme("#424242", "#FAFAFA"),
							titleColor: getColorBySystemTheme("#424242", "#FAFAFA"),
						},
						registered: {
							backgroundColor: getColorBySystemTheme("#F5F5F5", "#212121"),
							color: getColorBySystemTheme("#424242", "#A9A9A9"),
							successColor: getColorBySystemTheme("#4CAF50", "#4CAF50"),
						},
						counter: {
							color: getColorBySystemTheme("#424242", "#FAFAFA"),
							limitColor: getColorBySystemTheme("#424242", "#FAFAFA"),
							iconColor: getColorBySystemTheme("#616161", "#BDBDBD"),
							borderColor: getColorBySystemTheme("#61616160", "#BDBDBD60"),
							iconDisabledColor: getColorBySystemTheme(
								"#61616130",
								"#BDBDBD30",
							),
						},
						courtesy: {
							backgroundColor: getColorBySystemTheme("#F5F5F5", "#303030"),
							color: getColorBySystemTheme("#424242", "#FAFAFA"),
							successColor: getColorBySystemTheme("#4CAF50", "#4CAF50"),
						},
						soldOut: {
							backgroundColor: getColorBySystemTheme("#F5F5F5", "#303030"),
							messageColor: getColorBySystemTheme("#EF6C00", "#FFB74D"),
							color: getColorBySystemTheme("#424242", "#FAFAFA"),
						},
						comboAnnuity: {
							color: getColorBySystemTheme("#424242", "#FAFAFA"),
							backgroundColor: getColorBySystemTheme("#F5F5F5", "#252525"),
							checkboxColor: getColorBySystemTheme("#388E3C", "#66BB6A"),
							checkboxBackgroundColor: getColorBySystemTheme(
								"#FFFFFF",
								"#424242",
							),
							messageColor: getColorBySystemTheme("#616161", "#BDBDBD"),
							messageBackgroundColor: getColorBySystemTheme(
								"#E0E0E0",
								"#393939 ",
							),
							dashedBorderColor: getColorBySystemTheme("#BDBDBD", "#616161"),
						},
						pending: {
							color: getColorBySystemTheme("#FFFFFF", "#121212"),
							redirectColor: getColorBySystemTheme("#01579B", "#81D4FA"),
							backgroundColor: getColorBySystemTheme("#F57C00", "#F57C00"),
						},
						shoppingCart: {
							color: getColorBySystemTheme("#FFFFFF", "#121212"),
							redirectColor: getColorBySystemTheme("#01579B", "#81D4FA"),
							backgroundColor: getColorBySystemTheme("#FFAB00", "#FFAB00"),
						},
						amount: {
							color: getColorBySystemTheme("#424242", "#FFFFFF"),
							unavailableColor: getColorBySystemTheme("#424242", "#BDBDBD"),
							discountedAmount: getColorBySystemTheme("#757575", "#757575"),
						},
						discount: {
							color: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
							backgroundColor: getColorBySystemTheme("#f70e0e", "#f70e0e"),
						},
						unavailable: {
							color: getColorBySystemTheme("#424242", "#FFFFFF"),
							iconColor: getColorBySystemTheme("#FFB74D", "#FFB74D"),
							backgroundColor: getColorBySystemTheme("#EEEEEE", "#212121"),
						},
						loading: {
							color: getColorBySystemTheme("#424242", "#FFFFFF"),
							iconColor: getColorBySystemTheme("#424242", "#FFFFFF"),
							backgroundColor: getColorBySystemTheme("#EEEEEE", "#212121"),
						},
						category: {
							color: getColorBySystemTheme("#424242", "#FFFFFF"),
							taskColor: getColorBySystemTheme("#111C2B", "#EEEEEE"),
							borderColor: getColorBySystemTheme("#EEEEEE", "#424242"),
							warningColor: getColorBySystemTheme("#FFB74D", "#FFB74D"),
							backgroundColor: getColorBySystemTheme("#EEEEEE", "#303030"),
						},
						detail: {
							color: getColorBySystemTheme("#616161", "#FAFAFA"),
							backgroundColor: getColorBySystemTheme("#F3F3F3", "#212121"),
						},
						actionsButtons: {
							onSubscribe: {
								color: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
								backgroundColor: getColorBySystemTheme("#394354", "#394354"),
								backgroundColorHover: getColorBySystemTheme(
									"#394554",
									"#394554",
								),
							},
							onAddCart: {
								color: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
								backgroundColor: getColorBySystemTheme("#545F71", "#545F71"),
								backgroundColorHover: getColorBySystemTheme(
									"#546271",
									"#546271",
								),
							},
							onReturn: {
								color: getColorBySystemTheme("#424242", "#BDBDBD"),
								backgroundColor: getColorBySystemTheme(
									"transparent",
									"transparent",
								),
								backgroundColorHover: getColorBySystemTheme(
									"transparent",
									"transparent",
								),
							},
							onWatchStream: {
								color: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
								backgroundColor: getColorBySystemTheme("#FF5252", "#FF5252"),
								backgroundColorHover: getColorBySystemTheme(
									"#FF5452",
									"#FF5452",
								),
							},
							onWatchRecorded: {
								color: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
								backgroundColor: getColorBySystemTheme("#424242", "#424242"),
								backgroundColorHover: getColorBySystemTheme(
									"#424442",
									"#424442",
								),
							},
							onConfirm: {
								color: getColorBySystemTheme("#FFFFFF", "#212121"),
								backgroundColor: getColorBySystemTheme("#4CAF50", "#73D076"),
								backgroundColorHover: getColorBySystemTheme(
									"#4CB250",
									"#73D276",
								),
							},
							onWaitingList: {
								color: getColorBySystemTheme("#FFFFFF", "#212121"),
								backgroundColor: getColorBySystemTheme("#ffa726", "#ffb74d"),
								backgroundColorHover: getColorBySystemTheme(
									"#ffaa26",
									"#ffba4d",
								),
							},
						},
					},
					programmingSection: {
						item: {
							container: {
								backgroundColor: getColorBySystemTheme("#F9F9FF", "#232323"),
							},
							button: {
								backgroundColorHover: getColorBySystemTheme(
									"#6f93b5",
									"#545F61",
								),
								backgroundColor: getColorBySystemTheme("#6f93c5", "#545F71"),
								color: getColorBySystemTheme("#fff"),
							},
							time: {
								color: getColorBySystemTheme("#000", "#fff"),
							},
							information: {
								color: getColorBySystemTheme("#111C2B", "#fff"),
								colorBold: getColorBySystemTheme("#001C3A", "#fff"),
								typeActivityColor: getColorBySystemTheme("#545F71", "#999999"),
								defaultAxiosColor: getColorBySystemTheme(
									"#0c0c0ccc",
									"#f5f5f5cc",
								),
							},
							lecture: {
								backgroundColor: getColorBySystemTheme("#ffffff", "#232323"),
								dateTimeColor: getColorBySystemTheme("#545F71", "#999999"),
								buttonColor: getColorBySystemTheme("#3F80D6"),
							},
							speaker: {
								backgroundColor: getColorBySystemTheme("#FFFFFF", "#52525284"),
								nameColor: getColorBySystemTheme("#111C2B", "#FFFFFF"),
								paperColor: getColorBySystemTheme("#545F71", "#999999"),
								dot: getColorBySystemTheme("#E0E2EC", "#6D6D6D"),
								dotContent: getColorBySystemTheme("#000", "#fff"),
								dotSelected: getColorBySystemTheme("#6D6D6D", "#E3E3E3"),
								dotContentSelected: getColorBySystemTheme("#fff", "#000"),
							},
						},
						title: {
							color: getColorBySystemTheme("#000", "#fff"),
						},
					},
					default: {
						light: getColorBySystemTheme("#1f1f1f"),
						main: getColorBySystemTheme("#212121", "#ffffff"),
						dark: getColorBySystemTheme("#232323"),
						contrastText: getColorBySystemTheme("#ffffff"),
					},
					link: {
						main: getColorBySystemTheme("#771377", "#e38ee3"),
					},
					chip: {
						default: {
							color: getColorBySystemTheme("#424242"),
							text: getColorBySystemTheme("#ffffff"),
						},
						success: {
							color: getColorBySystemTheme("#4CAF50"),
							text: getColorBySystemTheme("#ffffff"),
						},
						warning: {
							color: getColorBySystemTheme("#EF6C00"),
							text: getColorBySystemTheme("#ffffff"),
						},
						danger: {
							color: getColorBySystemTheme("#f44336"),
							text: getColorBySystemTheme("#ffffff"),
						},
					},
					cardShopping: {
						paper: getColorBySystemTheme("#FFFFFF", "#191C20"),
						totalLabel: getColorBySystemTheme("#74777F", "#C3C6CF"),
						total: getColorBySystemTheme("#191C20", "#E1E2E9"),
					},
					listItensCart: {
						containerBackgroundColor: getColorBySystemTheme(
							"#F9F9FF",
							"#1D2024",
						),
						textColor: getColorBySystemTheme("#191C20", "#E1E2E9"),
						discountedColor: getColorBySystemTheme("#43474E", "#C3C6CF"),
					},
					cashbackWallet: {
						textColor: getColorBySystemTheme("#191C20", "#E1E2E9"),
						balanceColor: getColorBySystemTheme("#B818D2", "#B818D2"),
						redemptionLimitBackgroundColor: getColorBySystemTheme(
							"#F7D8FF",
							"#F7D8FF",
						),
						redemptionLimitColor: getColorBySystemTheme("#271430", "#271430"),
						buttonColor: getColorBySystemTheme("#513B59", "#513B59"),
						buttonHoverColor: getColorBySystemTheme("#511B59", "#511B59"),
						buttonTextColor: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
						labelColor: getColorBySystemTheme("#43474E", "#C3C6CF"),
						discountTextColor: getColorBySystemTheme("#271430", "#F7D8FF"),
					},
					chipCashback: {
						default: {
							color: getColorBySystemTheme("#E9DDFF", "#553F5D"),
							text: getColorBySystemTheme("#B818D2", "#F7D8FF"),
						},
						redemption: {
							color: getColorBySystemTheme("#E8F5E9", "#2E7D32"),
							text: getColorBySystemTheme("#2E7D32", "#E8F5E9"),
						},
					},
					sessionCashback: {
						iconColor: getColorBySystemTheme("#4F378A", "#4F378A"),
						backgroundColor: getColorBySystemTheme("#F9F9FF", "#272727"),
						iconBackgroundColor: getColorBySystemTheme("#CFBCFF", "#CFBCFF"),
						accumulation: {
							color: getColorBySystemTheme("#4F378A", "#efdfff"),
							amountColor: getColorBySystemTheme("#4F378A", "#efdfff"),
							prominenceColor: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
							prominenceBackgroundColor: getColorBySystemTheme(
								"#4F378A",
								"#4F578A",
							),
						},
						rescue: {
							color: getColorBySystemTheme("#256A4A", "#f0fff8"),
							amountColor: getColorBySystemTheme("#4D6356", "#4D8356"),
							prominenceColor: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
							prominenceBackgroundColor: getColorBySystemTheme(
								"#4D6356",
								"#4D8356",
							),
						},
					},
					AdherenceCampaign: {
						textColor: getColorBySystemTheme("#424242", "#F6EEFF"),
						titleColor: getColorBySystemTheme("#6750A4", "#B69DF7"),
						backgroundColor: getColorBySystemTheme("#F6EEFF", "#212121"),
						allowedButtonColor: getColorBySystemTheme("#6750A4", "#6750A4"),
						deniedButtonTextColor: getColorBySystemTheme("#8069BF", "#CFBCFF"),
						allowedButtonTextColor: getColorBySystemTheme("#FFFFFF", "#FFFFFF"),
					},
					type: systemTheme,
					background: {
						default: getColorBySystemTheme("#F9F9FF", "#1C1C1C"),
					},
					invert: {
						filter: `invert(${systemTheme === "light" ? 0 : 1})`,
					},
					scrollable: {
						arrowRight: {
							background: getColorBySystemTheme(
								"linear-gradient(90deg, rgba(249,249,255,0) 0%, rgba(249,249,255,0.469625350140056) 36%, rgba(249,249,255,1) 100%)",
								"linear-gradient(90deg, rgba(28,28,28,0) 0%, rgba(28,28,28,0.469625350140056) 36%, rgba(28,28,28,1) 100%)",
							),
						},
						arrowLeft: {
							background: getColorBySystemTheme(
								"linear-gradient(270deg, rgba(249,249,255,0) 0%, rgba(249,249,255,0.469625350140056) 36%, rgba(249,249,255,1) 100%)",
								"linear-gradient(270deg, rgba(28,28,28,0) 0%, rgba(28,28,28,0.469625350140056) 36%, rgba(28,28,28,1) 100%)",
							),
						},
					},
					fidelity: {
						tabColor: getColorBySystemTheme("#757575", "#A9A9A9"),
						tabColorActivity: getColorBySystemTheme("#191C20", "#FAFAFA"),
					},
					statement: {
						filter: {
							color: getColorBySystemTheme("#EDEDF4", "#EDEDF4"),
							textColor: getColorBySystemTheme("#191C20", "#191C20"),
							cashbackColor: getColorBySystemTheme("#0269C8", "#0269C8"),
							cashbackTextColor: getColorBySystemTheme("#FAFAFA", "#FAFAFA"),
						},
						group: {
							color: getColorBySystemTheme("#616161", "#EDEDF4"),
						},
					},
					campaign: {
						textColor: getColorBySystemTheme("#616161", "#EDEDF4"),
						titleColor: getColorBySystemTheme("#545F71", "#D8E3F8"),
						statusColor: getColorBySystemTheme("#1565C0", "#0269C8"),
						validityColor: getColorBySystemTheme("#74777F", "#C3C6CF"),
						adherenceColor: getColorBySystemTheme("#66BB6A", "#E8F5E9"),
						adherenceDateColor: getColorBySystemTheme("#6B7280", "#D8E3F8"),
						adherenceDateBackgroundColor: getColorBySystemTheme(
							"#D8E3F8",
							"#545F71",
						),
						AdherenceBackground: getColorBySystemTheme("#E8F5E9", "#66BB6A"),
					},
					wallet: {
						titleColor: getColorBySystemTheme("#424242", "#FAFAFA"),
						chip: {
							color: getColorBySystemTheme("#F6EEFF", "#422D5D"),
							textColor: getColorBySystemTheme("#9747FF", "#F7D8FF"),
							cashbackColor: getColorBySystemTheme("#F6EEFF", "#422D5D"),
							cashbackTextColor: getColorBySystemTheme("#9747FF", "#F7D8FF"),
							pointsColor: getColorBySystemTheme("#F6EEFF", "#422D5D"),
							pointsTextColor: getColorBySystemTheme("#9747FF", "#F7D8FF"),
						},
						balance: {
							textColor: getColorBySystemTheme("#757575", "#A9A9A9"),
						},
						consolidation: {
							amountColor: getColorBySystemTheme("#212121", "#F3F3F3"),
							statusColor: getColorBySystemTheme("#616161", "#949494"),
							textColor: getColorBySystemTheme("#757575", "#A9A9A9"),
						},
						latestMovements: {
							borderBottom: getColorBySystemTheme("#EEEEEE", "#303030"),
							seeMoreColor: getColorBySystemTheme("#616161", "#949494"),
							textColor: getColorBySystemTheme("#757575", "#A9A9A9"),
							descriptionColor: getColorBySystemTheme("#212121", "#F3F3F3"),
							amountColor: getColorBySystemTheme("#424242", "#D4D4D4"),
							icon: {
								default: {
									icon: getColorBySystemTheme("#757575", "#A9A9A9"),
									color: getColorBySystemTheme("#F5F5F5", "#252525"),
								},
								credit: {
									icon: getColorBySystemTheme("#66BB6A", "#E8F5E9"),
									color: getColorBySystemTheme("#E8F5E9", "#66BB6A"),
								},
								debit: {
									icon: getColorBySystemTheme("#EF5350", "#FFEBEE"),
									color: getColorBySystemTheme("#FFEBEE", "#EF5350"),
								},
							},
						},
					},
					quizResults: {
						correct: {
							main: getColorBySystemTheme("#4CAF50", "#81C784"),
							light: getColorBySystemTheme("#81C784", "#A5D6A7"),
							dark: getColorBySystemTheme("#388E3C", "#66BB6A"),
							contrastText: getColorBySystemTheme("#ffffff", "#000000cc"),
							background: getColorBySystemTheme(
								"rgba(76, 175, 80, 0.35)",
								"rgba(76, 175, 80, 0.2)",
							),
						},
						incorrect: {
							main: getColorBySystemTheme("#F44336", "#E57373"),
							light: getColorBySystemTheme("#E57373", "#EF9A9A"),
							dark: getColorBySystemTheme("#D32F2F", "#EF5350"),
							contrastText: getColorBySystemTheme("#ffffff", "#000000cc"),
							background: getColorBySystemTheme(
								"rgba(244, 67, 54, 0.35)",
								"rgba(244, 67, 54, 0.2)",
							),
						},

						chip: {
							background: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.02)",
								"rgba(255, 255, 255, 0.05)",
							),
							border: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.08)",
								"rgba(255, 255, 255, 0.08)",
							),
						},
						summary: {
							background: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.01)",
								"rgba(255, 255, 255, 0.02)",
							),
							border: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.08)",
								"rgba(255, 255, 255, 0.08)",
							),
						},
						score: {
							background: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.01)",
								"rgba(255, 255, 255, 0.02)",
							),
							border: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.08)",
								"rgba(255, 255, 255, 0.08)",
							),
							value: getColorBySystemTheme("#719d71", "#c8e6c9"),
							label: getColorBySystemTheme(
								"rgba(0, 0, 0, 0.61)",
								"rgba(255, 255, 255, 0.5)",
							),
						},
						headerBorder: getColorBySystemTheme("#E0E0E0", "#424242"),
						form: {
							backgroundColor: getColorBySystemTheme("#F3F3F3", "#333333"),
							color: getColorBySystemTheme("#000000", "#FFFFFF"),
						},
						question: {
							backgroundColor: getColorBySystemTheme("#FFFFFF", "#1A1A1A"),
							color: getColorBySystemTheme("#616161", "#BDBDBD"),
						},
					},
				},
				overrides: {
					MuiCheckbox: {
						root: {
							"&.Mui-checked": {
								color: getColorBySystemTheme("#00000099", "#ffffffb3"),
							},
						},
					},
					MuiRadio: {
						root: {
							color: getColorBySystemTheme("#0000008a", "#ffffffb3"),
						},
					},
					MuiButton: {
						root: {
							"&.Mui-disabled": {
								opacity: 0.5,
							},
						},
					},
					MuiInputBase: {
						root: {
							"& .MuiOutlinedInput-input:-webkit-autofill": {
								"-webkit-box-shadow": `0 0 0 100px ${getColorBySystemTheme(
									"#f3f3f3",
									"#1C1C1C",
								)} inset`,
								"-webkit-text-fill-color": getColorBySystemTheme(
									"#000000",
									"#ffffff",
								),
							},
						},
					},
					MuiPaper: {
						root: {
							backgroundColor: getColorBySystemTheme("#fff", "#232323"),
						},
					},
					MuiDialogTitle: {
						root: {
							backgroundColor: getColorBySystemTheme("#e0e0e0", "#424242"),
						},
					},
					MuiInputLabel: {
						root: {
							display: "-webkit-box",
							"-webkit-line-clamp": 1,
							"-webkit-box-orient": "vertical",
							wordBreak: "none",
							overflow: "hidden",
							hyphens: "auto",
							"--is-single-line": "1 - Clamp(0, Calc(1 - 1), 1)",
							"--delay": "Calc(-1s * (var(--is-single-line, 1) - 1))",
							animation: "states 1s var(--delay) paused",
						},
					},
					MuiTabs: {
						root: {
							minWidth: "auto !important",
							"& .MuiTab-wrapper": {
								padding: "8px",
								"& .MuiIcon-root": {
									fontSize: "20px !important",
								},
							},
							"& .Mui-selected": {
								fontWeight: "bold",
								"& .MuiTab-wrapper": {
									color: getColorBySystemTheme("#1976d2", "#fff"),
									borderBottom: "1px solid",
									borderBottomColor: getColorBySystemTheme(
										"#434343",
										"#EEEEEE",
									),
								},
							},
						},
					},
					MuiAlert: {
						standardSuccess: {
							backgroundColor: getColorBySystemTheme("#dff0d8", "#1e3a1e"),
							color: getColorBySystemTheme("#3c763d", "#9ae6b4"),
							"& .MuiAlert-icon": {
								fontSize: 20,
								color: getColorBySystemTheme("#3c763d", "#9ae6b4"),
							},
						},
						standardInfo: {
							backgroundColor: getColorBySystemTheme("#d9edf7", "#1e3a4e"),
							color: getColorBySystemTheme("#31708f", "#90cdf4"),
							"& .MuiAlert-icon": {
								fontSize: 20,
								color: getColorBySystemTheme("#31708f", "#90cdf4"),
							},
						},
						standardWarning: {
							backgroundColor: getColorBySystemTheme("#fcf8e3", "#4b3f1b"),
							color: getColorBySystemTheme("#8a6d3b", "#f6e05e"),
							"& .MuiAlert-icon": {
								fontSize: 20,
								color: getColorBySystemTheme("#8a6d3b", "#f6e05e"),
							},
						},
						standardError: {
							backgroundColor: getColorBySystemTheme("#f2dede", "#4a1c1c"),
							color: getColorBySystemTheme("#a94442", "#feb2b2"),
							"& .MuiAlert-icon": {
								fontSize: 20,
								color: getColorBySystemTheme("#a94442", "#feb2b2"),
							},
						},
					},
				},
			}),
		[systemTheme],
	);

	const {
		data: userCashbackPermissions,
		fetchData: fetchUserCashbackPermissions,
	} = useFetchApi(
		`${Config.baseURL}api/${SelectedLang}/cashback/permission?pessoa_id=${
			user?.data?.id ?? 0
		}&centro_custo_id=${centerCostId}`,
	);

	const { data: centerCost, fetchData: fetchCenterCost } = useFetchApi(
		Config.baseURL + `api/${SelectedLang}/entity/current/commerce`,
	);

	const { data: settingCenterCost, fetchData: fetchSettingCenterCost } =
		useFetchApi(
			Config.baseURL + `api/${SelectedLang}/center-cost/${centerCostId}`,
		);

	const { data: translation, fetchData: fetchTranslation } = useFetchApi(
		Config.baseURL +
			`api/${SelectedLang}/translation?centro_custo_id=${centerCostId}`,
	);

	const {
		data: configurationAnalytics,
		fetchData: fetchConfigurationAnalytics,
	} = useFetchApi(
		`${Config.baseURL}api/${SelectedLang}/setting?centro_custo_id=${centerCostId}&${configurator.ANALYTICS}`,
	);

	const getToken = useCallback(async () => {
		const data = {
			login: "icongressolive",
			senha: "FRB:y}|Y",
		};
		const body = JSON.stringify(data);
		
		await dispatch({
			type: "FETCH_STARTED",
			meta: {
				actions: auth.actions.AUTHENTICATE_REQUEST,
				body: body,
				url: Config.baseURL + `api/${SelectedLang}/authenticate`,
				method: "POST",
			},
		});
	}, []);

	useEffect(() => {
		if (centerCost?.data[0] && !centerCostId) {
			dispatch(
				auth.actions.setCenterCost(centerCost.data[0].centro_custo_padrao_id),
			);
			dispatch(setting.actions.setEntity(centerCost.data[0]));
		}
	}, [centerCost]);

	useEffect(() => {
		if (centerCostId) {
			fetchTranslation();
		}
	}, [SelectedLang, centerCostId]);

	useEffect(() => {
		if (translation?.data) {
			dispatch(language.actions.setTranslation(translation.data));
		}
	}, [translation]);

	useEffect(() => {
		if (userCashbackPermissions?.data && user?.data?.id) {
			dispatch(
				fidelity.actions.setUserPermissions(userCashbackPermissions?.data),
			);
		} else {
			dispatch(fidelity.actions.setUserPermissions(null));
		}
	}, [userCashbackPermissions?.data, user?.data?.id]);

	useEffect(() => {
		const list = async () => {
			await getToken();
		};
		list();
	}, [getToken]);

	useEffect(() => {
		if (token && token != "") {
			const list = async () => {
				await fetchCenterCost(null, null, { Authorization: `Bearer ${token}` });
				setStatus("released");
			};
			list();
		}
	}, [token]);

	useEffect(() => {
		if (token) {
			const userCashbackPermissions = async () => {
				await fetchUserCashbackPermissions(null, null, {
					Authorization: `Bearer ${token}`,
				});
			};
			userCashbackPermissions();
		}
	}, [token, user?.data?.id]);

	useEffect(() => {
		fetchSettingCenterCost();
		fetchConfigurationAnalytics();
	}, [centerCostId]);

	const analyticsInitialized = useRef(false);

	useEffect(() => {
		if (!configurationAnalytics?.data || analyticsInitialized.current) {
			return;
		}

		analyticsInitialized.current = true;

		const configurationAnalyticGTM = configurationAnalytics.data.find(
			(configuration) => String(configuration.codigo) === String(enumsSetting.ANALYTIC_GTM)
		);
		const configurationAnalyticMetaPixel = configurationAnalytics.data.find(
			(configuration) => String(configuration.codigo) === String(enumsSetting.ANALYTIC_META_PIXEL)
		);

		if (configurationAnalyticGTM?.valor_referencia) {
			try {
				TagManager.initialize({ gtmId: configurationAnalyticGTM.valor_referencia });

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({
					event: "page_view",
				});
			} catch (error) {
				console.error("Falha ao inicializar o GTM:", error);
			}
		}

		if (configurationAnalyticMetaPixel?.valor_referencia) {
			import("react-facebook-pixel")
				.then((ReactPixel) => {
					ReactPixel.default.init(configurationAnalyticMetaPixel.valor_referencia);
					ReactPixel.default.pageView();
				})
				.catch((error) => {
					console.error("Falha ao carregar react-facebook-pixel:", error);
				});
		}
	}, [configurationAnalytics?.data]);

	return (
		<ThemeProvider theme={theme}>
			<CssBaseline />
			<Router>
				<ToasterContainer />
				{status !== "released" && <LinearProgress />}
				{status === "released" && (
					<Switch>
						<RouteContainer path="/redirect/:cc">
							<RedirectContainer />
						</RouteContainer>
						<RouteContainer path="/login/:cc?">
							<LoginContainer />
						</RouteContainer>
						<RouteContainer path="/recovery-login/:cc?">
							<RecoveryLoginContainer />
						</RouteContainer>
						<RouteContainer path="/change-password/:cc?">
							<ChangePasswordContainer />
						</RouteContainer>
						<RouteContainer path="/offers">
							<RouterOffers />
						</RouteContainer>
						<RouteContainer path="/offers-unique/:cc/:activity_id?">
							<OffersUniqueContainer />
						</RouteContainer>
            <RouteContainer path="/lesson/:cc">
              <LessonContainer />
            </RouteContainer>
						<RouteContainer path="/ead">
							<EadContainer />
						</RouteContainer>
						<RouteContainer path="/detail-ead/:cc/:activity_schedule_id">
							<DetailEadContainer />
						</RouteContainer>
						<RouteContainer path="/store/:cc/">
							<StoreContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/shopping-cart">
							<ShoppingCartContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/my-activity">
							<MyActivityContainer />
						</RouteContainer>
						<RouteContainer path="/scientific-work/:cc">
							<ScientificWorkContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/my-course">
							<RouterCourse />
						</RouteContainer>
						<RouteContainer path="/my-lecture/:cc/:activity_schedule_id">
							<DetailMyLectureContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/streaming">
							<StreamingContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/me">
							<MeContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/panel">
							<PanelContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/new-panel">
							<NewPanelContainer />
						</RouteContainer>
						<RouteContainer isProtected path="/fidelity-dashboard">
							<FidelityDashboardContainer />
						</RouteContainer>
						<RouteContainer path="/payment/:id">
							<PaymentContainer />
						</RouteContainer>
						<RouteContainer path="/page/:id">
							<PageContainer />
						</RouteContainer>
						<RouteContainer path="/dasboard/:jwt/:page?">
							<DasboardContainer />
						</RouteContainer>
						<RouteContainer path="/lang/:lang/:url?">
							<DasboardContainer />
						</RouteContainer>
						<RouteContainer path="/:jwt?">
							<DasboardContainer />
						</RouteContainer>
					</Switch>
				)}
			</Router>
		</ThemeProvider>
	);
}

export default AppContainer;
